import { useEffect, useState } from 'react';
import { html, setTranslations, text } from 'lightning-common/lib/i18n';
import { MessageKey } from 'lightning-messages/lib/messages';

const importLocalizedMessages = (locale: string): Promise<any> => {
    const localeLowerCase = locale.toLowerCase();
    switch (localeLowerCase) {
        case 'ar':
            return import('lightning-messages/lib/messages/ar.json');
        case 'da':
            return import('lightning-messages/lib/messages/da.json');
        case 'de':
            return import('lightning-messages/lib/messages/de.json');
        case 'es':
            return import('lightning-messages/lib/messages/es.json');
        case 'fi':
            return import('lightning-messages/lib/messages/fi.json');
        case 'fr':
            return import('lightning-messages/lib/messages/fr.json');
        case 'he':
            return import('lightning-messages/lib/messages/he.json');
        case 'is':
            return import('lightning-messages/lib/messages/is.json');
        case 'it':
            return import('lightning-messages/lib/messages/it.json');
        case 'ja':
            return import('lightning-messages/lib/messages/ja.json');
        case 'ko':
            return import('lightning-messages/lib/messages/ko.json');
        case 'ms':
            return import('lightning-messages/lib/messages/ms.json');
        case 'nl':
            return import('lightning-messages/lib/messages/nl.json');
        case 'ru':
            return import('lightning-messages/lib/messages/ru.json');
        case 'sv':
            return import('lightning-messages/lib/messages/sv.json');
        case 'ta':
            return import('lightning-messages/lib/messages/ta.json');
        case 'tr':
            return import('lightning-messages/lib/messages/tr.json');
        case 'zh-hans':
            return import('lightning-messages/lib/messages/zh-Hans.json');
        case 'zh-hant':
            return import('lightning-messages/lib/messages/zh-Hant.json');
        default:
            if (localeLowerCase.includes('-')) {
                const localeLanguage = localeLowerCase.split('-')[0];
                return importLocalizedMessages(localeLanguage);
            }
            return import('lightning-messages/lib/messages/en.json');
    }
};

/**
 * Gets translated copy with encoded HTML.
 * @param name The translation key for the copy.
 * @param values Optional values to inject into placeholders in the copy.
 */
export const getHtmlCopy = (name: MessageKey, values?: {}) =>
    html(name, values);

/**
 * Gets translated copy with unencoded HTML. Used when your message includes HTML that should render.
 * @param name The translation key for the copy.
 * @param values Optional values to inject into placeholders in the copy.
 */
export const getTextCopy = (name: MessageKey, values?: {}) =>
    text(name, values);

export const useTranslations = (
    isReady: boolean,
    locale: string,
    tenant: number
) => {
    const [translationsLoaded, setTranslationsLoaded] =
        useState<boolean>(false);

    useEffect(() => {
        if (!isReady) return;

        setTranslationsLoaded(false);

        importLocalizedMessages(locale)
            .then((messages) => {
                setTranslations(messages, locale, tenant);
                setTranslationsLoaded(true);
            })
            .catch(() => {
                importLocalizedMessages('en').then((messages) => {
                    setTranslations(messages, locale, tenant);
                    setTranslationsLoaded(true);
                });
            });
    }, [isReady, locale, tenant]);

    return translationsLoaded;
};
